import {
    ONBOARDING_AGENT_DEFAULT_SCREEN_OFFSET,
    ONBOARDING_AGENT_PANEL_EXPANDED_HEIGHT_OFFSET,
    ONBOARDING_AGENT_PANEL_HEIGHT,
    ONBOARDING_AGENT_PANEL_WIDTH,
    ONBOARDING_AGENT_TRIGGER_SIZE,
    ONBOARDING_AGENT_VIEWPORT_MARGIN,
} from "../constants";
import type { OnboardingAgentViewportPosition } from "../OnboardingAgent.types";

export type OnboardingAgentViewportSize = {
    readonly width: number;
    readonly height: number;
};

export const getViewportSize = (): OnboardingAgentViewportSize => ({
    width: window.innerWidth,
    height: window.innerHeight,
});

export const getCompactPanelHeight = (): number => ONBOARDING_AGENT_PANEL_HEIGHT;

export const getExpandedPanelHeight = (): number =>
    Math.max(ONBOARDING_AGENT_PANEL_HEIGHT, getViewportSize().height - ONBOARDING_AGENT_PANEL_EXPANDED_HEIGHT_OFFSET);

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

const clampPositionToViewport = (
    position: OnboardingAgentViewportPosition,
    width: number,
    height: number,
): OnboardingAgentViewportPosition => {
    const viewport = getViewportSize();
    const maxX = Math.max(ONBOARDING_AGENT_VIEWPORT_MARGIN, viewport.width - width - ONBOARDING_AGENT_VIEWPORT_MARGIN);
    const maxY = Math.max(
        ONBOARDING_AGENT_VIEWPORT_MARGIN,
        viewport.height - height - ONBOARDING_AGENT_VIEWPORT_MARGIN,
    );

    return {
        x: clamp(position.x, ONBOARDING_AGENT_VIEWPORT_MARGIN, maxX),
        y: clamp(position.y, ONBOARDING_AGENT_VIEWPORT_MARGIN, maxY),
    };
};

export const createDefaultTriggerPosition = (): OnboardingAgentViewportPosition => {
    const viewport = getViewportSize();

    return clampPositionToViewport(
        {
            x: viewport.width - ONBOARDING_AGENT_TRIGGER_SIZE - ONBOARDING_AGENT_DEFAULT_SCREEN_OFFSET,
            y: viewport.height - ONBOARDING_AGENT_TRIGGER_SIZE - ONBOARDING_AGENT_DEFAULT_SCREEN_OFFSET,
        },
        ONBOARDING_AGENT_TRIGGER_SIZE,
        ONBOARDING_AGENT_TRIGGER_SIZE,
    );
};

// Fraction of the available track within which an element is treated as
// "docked" to an edge. Inside this zone we preserve the exact pixel distance
// from that edge; outside of it the element is considered free-floating.
const EDGE_DOCK_RATIO = 0.25;

const reflowCoordinateForViewportResize = (
    coordinate: number,
    elementSize: number,
    previousViewportSize: number,
    nextViewportSize: number,
): number => {
    const availablePrevious = previousViewportSize - elementSize;
    const availableNext = nextViewportSize - elementSize;

    if (availablePrevious <= 0) {
        return coordinate;
    }

    const distanceFromStartEdge = coordinate;
    const distanceFromEndEdge = availablePrevious - coordinate;
    const dockThreshold = availablePrevious * EDGE_DOCK_RATIO;

    // Docked near the start edge: preserve the exact distance from it so a
    // corner/edge-parked element (e.g. the bottom-right trigger) stays pinned
    // and the position is fully reversible when the viewport is restored.
    if (distanceFromStartEdge <= distanceFromEndEdge && distanceFromStartEdge <= dockThreshold) {
        return distanceFromStartEdge;
    }

    // Docked near the end edge: preserve the exact distance from it.
    if (distanceFromEndEdge < distanceFromStartEdge && distanceFromEndEdge <= dockThreshold) {
        return availableNext - distanceFromEndEdge;
    }

    // Free-floating in the middle: preserve the element's relative position so
    // it keeps its place (e.g. a centered trigger stays centered) instead of
    // jumping toward an edge as the viewport changes.
    const centerRatio = (coordinate + elementSize / 2) / previousViewportSize;

    return centerRatio * nextViewportSize - elementSize / 2;
};

export const reflowPositionForViewportResize = (
    position: OnboardingAgentViewportPosition,
    previousViewport: OnboardingAgentViewportSize,
    elementWidth: number,
    elementHeight: number,
): OnboardingAgentViewportPosition => {
    const nextViewport = getViewportSize();

    if (previousViewport.width <= 0 || previousViewport.height <= 0) {
        return clampPositionToViewport(position, elementWidth, elementHeight);
    }

    return clampPositionToViewport(
        {
            x: reflowCoordinateForViewportResize(position.x, elementWidth, previousViewport.width, nextViewport.width),
            y: reflowCoordinateForViewportResize(
                position.y,
                elementHeight,
                previousViewport.height,
                nextViewport.height,
            ),
        },
        elementWidth,
        elementHeight,
    );
};

export const createVisiblePanelPosition = (
    triggerPosition: OnboardingAgentViewportPosition,
    panelHeight: number = ONBOARDING_AGENT_PANEL_HEIGHT,
): OnboardingAgentViewportPosition =>
    clampPositionToViewport(
        {
            x: triggerPosition.x + ONBOARDING_AGENT_TRIGGER_SIZE - ONBOARDING_AGENT_PANEL_WIDTH,
            y: triggerPosition.y + ONBOARDING_AGENT_TRIGGER_SIZE - panelHeight,
        },
        ONBOARDING_AGENT_PANEL_WIDTH,
        panelHeight,
    );
